import { MeetingType } from '../types/sessionContext';
import { SessionContextService } from './sessionContextService';
import { SessionService } from './sessionService';
import { SALES_STAGES, getSalesStageMapping } from '../constants';

export interface SalesStageMapping {
  label: string;
  description: string;
}

class SalesStageService {
  private static cache: MeetingType[] | null = null;
  private static cacheExpiry: number = 0;
  private static readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Get meeting types for current session with caching
   */
  static async getMeetingTypes(): Promise<MeetingType[]> {
    const now = Date.now();
    
    // Return cached data if still valid
    if (this.cache && now < this.cacheExpiry) {
      return this.cache;
    }

    try {
      const sessionId = SessionService.getSessionId();
      const sessionContext = await SessionContextService.getSessionContext(sessionId);
      
      if (sessionContext && sessionContext.meetingTypes && Array.isArray(sessionContext.meetingTypes) && sessionContext.meetingTypes.length > 0) {
        this.cache = sessionContext.meetingTypes;
      } else {
        // Fallback to default meeting types
        try {
          const defaultMeetingTypes = await SessionContextService.getDefaultMeetingTypes();
          this.cache = Array.isArray(defaultMeetingTypes) ? defaultMeetingTypes : this.getFallbackMeetingTypes();
        } catch {
          this.cache = this.getFallbackMeetingTypes();
        }
      }
      
      this.cacheExpiry = now + this.CACHE_DURATION;
      return this.cache;
    } catch (error) {
      // If all fails, use static fallback
      this.cache = this.getFallbackMeetingTypes();
      this.cacheExpiry = now + this.CACHE_DURATION;
      return this.cache;
    }
  }

  /**
   * Get sales stage mapping (dynamic version of getSalesStageMapping)
   */
  static async getSalesStageMapping(stage: string): Promise<SalesStageMapping> {
    try {
      const meetingTypes = await this.getMeetingTypes();
      
      // Look for exact match by name
      const matchedType = meetingTypes.find(type => type.name === stage);
      if (matchedType) {
        return {
          label: matchedType.name,
          description: matchedType.description || 'Niestandardowy typ spotkania'
        };
      }

      // Fallback to static mapping
      const staticMapping = getSalesStageMapping(stage);
      return staticMapping;
    } catch (error) {
      // Ultimate fallback
      return {
        label: stage,
        description: 'Nieznany etap sprzedaży'
      };
    }
  }

  /**
   * Get sales stage label (dynamic version for HistoryPage)
   */
  static async getSalesStageLabel(stage: string): Promise<string> {
    const mapping = await this.getSalesStageMapping(stage);
    return mapping.label;
  }

  /**
   * Clear cache (useful when meeting types are updated)
   */
  static clearCache(): void {
    this.cache = null;
    this.cacheExpiry = 0;
  }

  /**
   * Get fallback meeting types from static SALES_STAGES
   */
  private static getFallbackMeetingTypes(): MeetingType[] {
    return SALES_STAGES.map((stage, index) => ({
      id: `fallback-${index}`,
      name: stage,
      description: getSalesStageMapping(stage).description
    }));
  }

  /**
   * Check if meeting types are from session context (not fallback)
   */
  static async areCustomMeetingTypes(): Promise<boolean> {
    try {
      const sessionId = SessionService.getSessionId();
      const sessionContext = await SessionContextService.getSessionContext(sessionId);
      return sessionContext && sessionContext.meetingTypes && Array.isArray(sessionContext.meetingTypes) && sessionContext.meetingTypes.length > 0;
    } catch {
      return false;
    }
  }
}

export { SalesStageService };